import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PageHeader, Pager, StatusBadge } from '@/components/kit';
import { FilterSelect, ListBody, Toolbar } from '@/components/inputs';
import TicketDialog from '@/components/account/TicketDialog';
import { PRIORITY_STYLES, TICKET_STATUSES, TICKET_STATUS_STYLES } from '@/shared/constants';
import { TICKET_FIELDS } from '@/shared/fields';
import { paged } from '@/shared/data';
import { formatRelativeTime } from '@/utils/date';

const { eq } = window.ApperSDK.CoreSDK;

export const route = { path: '/account/tickets', layout: 'owner', access: "profile === 'Customer_c'" };
export const nav = { icon: 'LifeBuoy', label: 'Support', section: 'My account', profiles: ['Customer_c'], order: 30 };

const PAGE_SIZE = 10;

export default function Tickets() {
  const [params, setParams] = useSearchParams();
  const orderParam = params.get('order');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (orderParam) setOpen(true);
  }, [orderParam]);

  useEffect(() => {
    setPage(1);
  }, [status]);

  const { data, loading, error, run } = useFetch(() => {
    let q = sdk.table('tickets_c').select(TICKET_FIELDS);
    if (status !== 'all') q = q.where(eq('status_c', status));
    return q.orderByDesc('last_reply_at_c').page(page, PAGE_SIZE).fetch().then(paged);
  }, [status, page]);

  const list = data?.rows ?? [];

  function onOpenChange(next) {
    setOpen(next);
    if (!next && orderParam) setParams({}, { replace: true });
  }

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        eyebrow="My account"
        title="Support"
        description="Questions about setup, billing or playback. We answer in the thread."
        actions={
          <Button onClick={() => setOpen(true)}>
            <ApperIcon name="Plus" size={16} />
            New ticket
          </Button>
        }
      />

      <Toolbar>
        <FilterSelect value={status} onChange={setStatus} values={TICKET_STATUSES} allLabel="All tickets" />
      </Toolbar>

      <ListBody
        loading={loading}
        data={data}
        error={error}
        run={run}
        isEmpty={!list.length}
        empty={{
          icon: 'LifeBuoy',
          title: status === 'all' ? 'No tickets yet' : `No ${status.toLowerCase()} tickets`,
          description: status === 'all' ? 'Stuck on something? Open a ticket and we will take it from there.' : 'Try another filter.',
          action: status === 'all' ? () => setOpen(true) : undefined,
          actionLabel: status === 'all' ? 'Open a ticket' : undefined,
        }}
      >
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Subject</TableHead>
                <TableHead className="hidden md:table-cell">Order</TableHead>
                <TableHead className="hidden md:table-cell">Category</TableHead>
                <TableHead className="hidden lg:table-cell">Priority</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="hidden sm:table-cell">Last reply</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((t) => (
                <TableRow key={t.Id}>
                  <TableCell className="max-w-[18rem]">
                    <Link to={`/account/tickets/${t.Id}`} className="font-medium text-primary hover:underline">
                      {t.Name}
                    </Link>
                    <p className="text-xs text-muted-foreground sm:hidden">{formatRelativeTime(t.last_reply_at_c || t.CreatedOn)}</p>
                  </TableCell>
                  <TableCell className="hidden font-mono text-sm md:table-cell">{t.order_c?.Name ?? '—'}</TableCell>
                  <TableCell className="hidden text-muted-foreground md:table-cell">{t.category_c}</TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <StatusBadge value={t.priority_c} styles={PRIORITY_STYLES} />
                  </TableCell>
                  <TableCell>
                    <StatusBadge value={t.status_c} styles={TICKET_STATUS_STYLES} />
                  </TableCell>
                  <TableCell className="hidden text-muted-foreground sm:table-cell">
                    {formatRelativeTime(t.last_reply_at_c || t.CreatedOn)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <Pager page={page} total={data?.total} pageSize={PAGE_SIZE} hasMore={data?.hasMore} onPage={setPage} />
      </ListBody>

      <TicketDialog open={open} onOpenChange={onOpenChange} defaultOrder={orderParam} onSaved={run} />
    </div>
  );
}
